import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useInView } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const DiagramTeaser = ({ source, slug, variant = "desktop" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "200px" });
  const [svg, setSvg] = useState("");

  useEffect(() => {
    if (!inView || !source) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "dark",
          themeVariables: {
            background: "#0A0A0A",
            primaryColor: "#141414",
            primaryTextColor: "#FAFAFA",
            primaryBorderColor: "rgba(230, 228, 217, 0.35)",
            lineColor: "#8a8a8a",
            secondaryColor: "#101010",
            tertiaryColor: "#0A0A0A",
          },
        });
        const result = await mermaid.render(`teaser-${variant}-${slug}`, source);
        if (!cancelled) setSvg(result.svg);
      } catch (e) {
        /* teaser is decorative — fail silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inView, source, slug, variant]);

  if (variant === "mobile") {
    return (
      <div
        ref={ref}
        data-testid={`insight-teaser-mobile-${slug}`}
        aria-hidden="true"
        className="relative flex lg:hidden items-start h-20 mb-8 overflow-hidden opacity-30 [&_svg]:w-full [&_svg]:h-auto"
      >
        <div dangerouslySetInnerHTML={{ __html: svg }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid={`insight-teaser-${slug}`}
      aria-hidden="true"
      className="relative hidden lg:flex lg:col-span-2 items-start h-28 overflow-hidden opacity-25 group-hover:opacity-45 transition-opacity duration-700 [&_svg]:w-full [&_svg]:h-auto"
    >
      <div dangerouslySetInnerHTML={{ __html: svg }} />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#050505]" />
    </div>
  );
};

export default function InsightsSection() {
  const [posts, setPosts] = useState([]);
  const [diagrams, setDiagrams] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${API}/diagrams`)
      .then((res) =>
        setDiagrams(
          Object.fromEntries(res.data.map((d) => [d.slug, d.source]))
        )
      )
      .catch(() => {});
  }, []);

  return (
    <section
      id="insights"
      data-testid="insights-section"
      className="px-6 lg:px-12 py-24 lg:py-32"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
            05 — Insights &amp; Blueprints
          </p>
          <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
            Proof by Architecture,{" "}
            <span className="italic text-[#E6E4D9]">Not Testimonials</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/60">
            Anonymized engagement architectures with quantified outcomes. No
            client names, no logos — the diagrams and the numbers carry the proof.
          </p>
        </Reveal>

        <Reveal>
          <div
            data-testid="blueprints-reading-guide"
            className="mt-14 border border-white/10 bg-white/[0.02] p-8 lg:p-10"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] mb-5">
              How to read these Blueprints
            </p>
            <p className="text-sm sm:text-base font-light leading-relaxed text-white/60 max-w-3xl">
              Blueprints are anonymized composites or engagement-derived
              patterns designed to protect confidential client information.
              Quantified results are labeled as measured engagement outcomes,
              customer-reported outcomes, modeled business cases, or
              illustrative benchmarks. Architecture patterns are reusable;
              results are not guarantees and vary by source systems, operating
              conditions, implementation scope, and customer adoption.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  term: "Blueprint",
                  def: "A reusable technical-commercial architecture pattern for a defined enterprise risk or use case.",
                },
                {
                  term: "Pattern Study",
                  def: "A cross-industry operating thesis connecting enterprise objections, architecture patterns, and commercial implications.",
                },
                {
                  term: "Insight",
                  def: "A shorter point of view or advisory note on one issue, decision, or recommendation.",
                },
                {
                  term: "Field Note",
                  def: "An anonymized lesson from an enterprise pursuit, technical validation, delivery program, or executive decision room.",
                },
              ].map((t) => (
                <div key={t.term}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 mb-2">
                    {t.term}
                  </p>
                  <p className="text-xs font-light leading-relaxed text-white/45">
                    {t.def}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-14 lg:mt-20 border-y border-white/10 divide-y divide-white/10">
          {[
            {
              to: "/patterns/medallion-spine",
              title: "Same Spine, Two Budgets",
              sub: "one medallion architecture, the legal budget and the trade-spend P&L",
              testId: "insights-pattern-study-medallion",
            },
            {
              to: "/patterns/cdc-deal-closer",
              title: "One Stream, Three Deals",
              sub: "log-based CDC plus a control model answers the production, custody, and cutover objections",
              testId: "insights-pattern-study-cdc",
            },
          ].map((study) => (
            <Reveal key={study.to}>
              <Link
                to={study.to}
                data-testid={study.testId}
                aria-label={`Pattern Study: ${study.title} — ${study.sub}`}
                className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 py-8 px-2 lg:px-4 bg-[#E6E4D9]/[0.03] hover:bg-[#E6E4D9]/[0.06] transition-colors duration-500"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] shrink-0">
                  Pattern Study
                </span>
                <span className="font-serif text-xl sm:text-2xl text-white tracking-tight">
                  {study.title} —{" "}
                  <span className="italic text-[#E6E4D9]">{study.sub}</span>
                </span>
                <ArrowUpRight
                  size={20}
                  weight="light"
                  className="sm:ml-auto shrink-0 text-white/25 transition-all duration-500 group-hover:text-[#E6E4D9] group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="border-b border-white/10 divide-y divide-white/10">
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="py-12 lg:py-14 px-2 lg:px-4 animate-pulse">
                <div className="h-3 w-32 bg-white/10 mb-5" />
                <div className="h-8 w-2/3 bg-white/10 mb-4" />
                <div className="h-4 w-1/2 bg-white/5" />
              </div>
            ))}

          {!loading && posts.length === 0 && (
            <p
              data-testid="insights-empty"
              className="py-14 px-2 lg:px-4 font-mono text-xs uppercase tracking-[0.25em] text-white/40"
            >
              New insights publishing soon.
            </p>
          )}

          {!loading &&
            posts.map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.08} y={24}>
                <Link
                  to={`/insights/${post.slug}`}
                  data-testid={`insight-row-${post.slug}`}
                  aria-label={`${post.category}: ${post.title} — ${formatDate(post.published_at)}, ${post.read_time}`}
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 py-12 lg:py-14 px-2 lg:px-4 hover:bg-white/[0.02] transition-colors duration-500"
                >
                  <DiagramTeaser source={diagrams[post.slug]} slug={post.slug} variant="mobile" />
                  <div className="lg:col-span-3 flex lg:flex-col gap-3 lg:gap-4 items-start">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]">
                      {post.category}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      {formatDate(post.published_at)} · {post.read_time}
                    </span>
                  </div>
                  <div className="lg:col-span-6">
                    <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-snug mb-4 group-hover:text-[#E6E4D9] transition-colors duration-300">
                      {post.title}
                    </h3>
                    <p className="text-base font-light leading-relaxed text-white/60 max-w-2xl">
                      {post.excerpt}
                    </p>
                  </div>
                  <DiagramTeaser source={diagrams[post.slug]} slug={post.slug} />
                  <div className="hidden lg:flex lg:col-span-1 items-start justify-end">
                    <ArrowUpRight
                      size={30}
                      weight="light"
                      className="text-white/25 transition-all duration-500 group-hover:text-[#E6E4D9] group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
}
